/**
 * Auth and access-control contract (S02).
 *
 * The browser never receives a token — the session is an httpOnly cookie pair —
 * so nothing here describes credentials. These are the shapes the app needs to
 * render an identity and manage who may sign in.
 */
import { z } from 'zod';

/** Loose email check: the IdP is authoritative, we only reject obvious junk. */
const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(320)
  .regex(/^[^\s@]+@[^\s@.]+\.[^\s@]+$/, 'must be an email address');

/**
 * The signed-in user, as the sidebar shows them (S03: "verified identity").
 *
 * `emailClaim` records which OIDC claim the address came from. Schwarz's IdP
 * publishes `mail` rather than `email`, and a future configuration change could
 * move it again — recording the source turns "why is this address wrong?" into a
 * one-line answer.
 */
export const meSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  name: z.string().nullable(),
  pictureUrl: z.string().nullable(),
  emailClaim: z.string().nullable(),
  lastLoginAt: z.string().datetime().nullable(),

  /**
   * Whether this deployment exposes the test tools (the hidden reset that
   * clears call history so QA can re-run a scenario).
   *
   * A property of the environment rather than of the person, so it sits oddly
   * beside an identity — but this is the one call the shell already makes on
   * every load, and the alternative is a second request whose only job is to
   * carry one boolean. Where it is false the browser never renders the gesture,
   * and the endpoint answers 404 regardless: this decides what is drawn, never
   * what is allowed.
   */
  testTools: z.boolean(),
});
export type Me = z.infer<typeof meSchema>;

/** An entry in the domain-restricted allowlist. */
export const allowedEmailSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
  addedBy: z
    .object({ id: z.string().uuid(), email: z.string(), name: z.string().nullable() })
    .nullable(),
  /**
   * True when this entry is the signed-in user's own. The UI disables removal
   * rather than letting the request fail — one of the two lockout guards.
   */
  isSelf: z.boolean(),
});
export type AllowedEmail = z.infer<typeof allowedEmailSchema>;

export const createAllowedEmailSchema = z.object({
  email: emailSchema,
  note: z.string().trim().max(200).optional(),
});
export type CreateAllowedEmail = z.infer<typeof createAllowedEmailSchema>;

/**
 * Revoking several entries at once.
 *
 * One request rather than a DELETE per row, because the lockout guards have to
 * consider the whole set: removing four of five entries is fine, removing all
 * five is not. A loop of single deletes would happily delete the first four and
 * then fail, which is the worst of both outcomes — the caller asked for all of
 * them and got a partial result with no obvious way back.
 */
export const bulkRemoveAllowedEmailsSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(200),
});
export type BulkRemoveAllowedEmails = z.infer<typeof bulkRemoveAllowedEmailsSchema>;

/**
 * Why a sign-in attempt was refused.
 *
 * Distinct codes because the remedies differ: a wrong domain is permanent and a
 * missing allowlist entry is something a colleague can fix in a minute. S02 asks
 * for errors that are "clear and recoverable", which means saying which it is.
 */
export const authErrorCodeSchema = z.enum([
  'DOMAIN_NOT_ALLOWED',
  'NOT_ON_ALLOWLIST',
  'EMAIL_NOT_VERIFIED',
  'NO_EMAIL_CLAIM',
  'STATE_MISMATCH',
  'PROVIDER_ERROR',
]);
export type AuthErrorCode = z.infer<typeof authErrorCodeSchema>;

/** Human-readable, safe to show. Deliberately never names the allowlist owner. */
export const authErrorMessages: Record<AuthErrorCode, string> = {
  DOMAIN_NOT_ALLOWED: 'That account is outside the organisations allowed to use this console.',
  NOT_ON_ALLOWLIST:
    'Your account is not on the access list yet. Ask a colleague who already has access to add you.',
  EMAIL_NOT_VERIFIED: 'Your identity provider reports this address as unverified.',
  NO_EMAIL_CLAIM:
    'Your identity provider did not return an email address, so access cannot be checked.',
  STATE_MISMATCH: 'The sign-in attempt expired or was tampered with. Please try again.',
  PROVIDER_ERROR: 'The identity provider could not complete the sign-in. Please try again.',
};

/** Where the app returns to after a successful sign-in. */
export const loginQuerySchema = z.object({
  /**
   * Relative paths only. An absolute URL here would make the login endpoint an
   * open redirect, which is a phishing primitive rather than a convenience.
   */
  returnTo: z
    .string()
    .startsWith('/')
    .max(512)
    .refine((v) => !v.startsWith('//'), 'must be a path, not a protocol-relative URL')
    .optional(),
});
export type LoginQuery = z.infer<typeof loginQuerySchema>;
